import moment from 'moment';
import { prepareReorder } from './ordered-relation';
import { getChildren } from './reactive-cache';
import { isoDateFormat } from './time-utils';

// Client ID and API key from the Developer Console
var CLIENT_ID = '252405708756-ioo68o79ke0lg76lh68tc0lnct306los.apps.googleusercontent.com';
var API_KEY = 'AIzaSyCRm_x6Z3pVtKMAugmtojPOkVtNHQHtxa4';

// Array of API discovery doc URLs for APIs used by the quickstart
var DISCOVERY_DOCS = ["https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest"];

// Authorization scopes required by the API; multiple scopes can be
// included, separated by spaces.
var SCOPES = "https://www.googleapis.com/auth/calendar";


let rootVue = null;


export async function initGoogleApi(vue) {
  rootVue = vue;

  const script = document.createElement('script');
  script.src = 'https://apis.google.com/js/api.js';
  script.id = 'googleApi';
  document.body.appendChild(script);

  script.addEventListener('error', () => { console.log("Gapi script error") });
  script.addEventListener('abort', () => { console.log("Gapi script aborted") });
  script.addEventListener('load', async () => {
    console.log("Google API script loaded.");
    gapi.load('client:auth2', initClient);
  });
}

/**
  *  Initialize the API client library and set up sign-in state listeners.
  */
export function initClient() {
  gapi.client.init({
    apiKey: API_KEY,
    clientId: CLIENT_ID,
    discoveryDocs: DISCOVERY_DOCS,
    scope: SCOPES
  }).then(function () {
    // Listen for sign-in state changes.
    gapi.auth2.getAuthInstance().isSignedIn.listen(updateSignInStatus);

    // Handle the initial sign-in state.
    updateSignInStatus(gapi.auth2.getAuthInstance().isSignedIn.get());
  }, function (error) {
    console.log(JSON.stringify(error, null, 2));
  });
}

/**
  *  Called when the sign-in status changes, to update the UI etc.
  */
async function updateSignInStatus(isSignedIn) {
  if (rootVue) {
    rootVue.signedInToGoogle = isSignedIn;
    console.log("signedInToGoogle:", rootVue.signedInToGoogle);
  } else {
    console.error("No root vue instance provided.");
  }
  if (isSignedIn) {
    await loadCalendarInfo();
    if (rootVue.$settings) loadEvents();
  }
}

export function googleSignIn() {
  gapi.auth2.getAuthInstance().signIn();
}

export function googleSignOut() {
  gapi.auth2.getAuthInstance().signOut();
}

/**
 * Load calendar data and initialize sync settings if not defined.
 */
export async function loadCalendarInfo() {
  let changed = false
  if (!rootVue.$settings.calendarSync) {
    rootVue.$settings.calendarSync = {}
    changed = true
  }

  const response = await gapi.client.calendar.calendarList.list({})
  const calendars = response.result.items
  console.debug('User calendars:', calendars)

  for (const cal of calendars) {
    rootVue.googleCalendars[cal.id] = cal
    if (cal.primary && !(cal.id in rootVue.$settings.calendarSync)) {
      rootVue.$settings.calendarSync[cal.id] = true;
      changed = true
    }
  }

  if (changed) await rootVue.$settings.save();
}

/**
 * Main routine for getting events from calendar into the database.
 */
export async function loadEvents() {
  console.log(`Loading events...`);
  const docs = [];

  for (const calId of Object.keys(rootVue.googleCalendars)) {
    const enabled = rootVue.$settings.calendarSync[calId]
    console.log(`Loading events from ${calId}: ${enabled}`)
    if (!!enabled) docs.push(...await loadEventsFromCalendar(calId));
  }

  const eventsByDate = groupAndSortByTime(docs);
  const withPlannedFor = [];

  for (const [startDay, items] of Object.entries(eventsByDate)) {
    withPlannedFor.push(...prepareReorder({
      relation: "plannedFor",
      reactiveCache: rootVue.$reactiveCache,
      insertObjOrObjs: items,
      insertPos: 0,
      newPPtr: {
        parentClass: 'day',
        parentKey: startDay,
      },
      skipOrdered: false,
    }));
  }
  console.debug("Event docs to save:", withPlannedFor);
  rootVue.$defaultDB.bulkDocs(withPlannedFor);
}

/**
 * Load event data, calculate relation pointers and save event items.
 */
async function loadEventsFromCalendar(calendarId) {
  const data = await getEventData(calendarId, 10, 30);
  if (!data.length) {
    console.log("No events within last 10 and next 30 days.")
    return [];
  }
  return parseEventData(data, calendarId);
}

/**
 * Get "raw" event data from Google Calendar API.
 */
async function getEventData(calendarId, daysBehind, daysAhead) {
  const behind = moment().startOf('day').subtract(daysBehind, 'days').format();
  const ahead = moment().startOf('day').add(daysAhead, 'days').format();

  const response = await gapi.client.calendar.events.list({
    'calendarId': calendarId,
    'timeMin': behind,
    'timeMax': ahead,
    'showDeleted': true,
    'singleEvents': true,
    'orderBy': 'startTime'
  });

  console.log("events.list response:", response.result);
  return response.result.items;
}

/**
 * Construct organizer-like docs from "raw" event data.
 */
function parseEventData(events, calendarId) {
  const docs = [];

  for (const event of events) {
    // Event was exported from an Organizer item
    if (event?.extendedProperties?.shared?.organizerId) {
      continue;
    }
    const doc = {
      _id: `gcal:${event.id}`,
      name: event.summary,
      type: 'event',
      relation: 'subtaskOf',
      event,
      calendarId,
    }
    // If the item already exists in couch, we'll need the rev to update it.
    if (rootVue.$reactiveCache.docs[doc._id]) {
      doc._rev = rootVue.$reactiveCache.docs[doc._id]._rev
    }

    // Mark removed events for deletion, unless they have notes or subtasks.
    const hasChildren = !!getChildren({
      cache: rootVue.$reactiveCache,
      relation: 'subtaskOf',
      pClass: 'task',
      pKey: doc._id,
    }).length;
    if (event.status === "cancelled" && !hasChildren) {
      doc._deleted = true;
    }

    docs.push(doc);
  }

  return docs;
}

/**
 * Return a dictionary of events grouped by date, each group sorted by start time.
 */
function groupAndSortByTime(events) {
  const eventsByDate = {}
  for (const doc of events) {
    const startDay = doc.event.start.dateTime
      ? doc.event.start.dateTime.split('T')[0]
      : doc.event.start.date;

    if (!eventsByDate[startDay]) eventsByDate[startDay] = []
    eventsByDate[startDay].push(doc);
  }
  for (const [startDay, items] of Object.entries(eventsByDate)) {
    const sorted = []
    for (const i of items) {
      if (i.event.start.date) sorted.push(i)
    }
    const nonfullday = items.filter(doc => doc.event.start.dateTime)
    nonfullday.sort((doc1, doc2) => doc1.event.start.dateTime < doc2.event.start.dateTime ? -1 : 1)
    sorted.push(...nonfullday);
    eventsByDate[startDay] = sorted
  }

  return eventsByDate;
}

/**
 * Used when adding notes/subtasks to an event.
 */
export function addOrganizerLinkToDescription(item) {
  const calInfo = rootVue.googleCalendars[item.calendarId]
  if (calInfo.accessRole !== "owner") {
    console.log("Skipping event update, not an owner...")
    return
  }
  const link = `${window.location.origin}/app/task/${item._id}`;
  const currDesc = item.event.description;

  if (currDesc?.includes(`href="${link}"`)) {
    console.log("Link to Organizer already present in event description.")
    return
  };
  const linkWithText = `See notes attached to this event in <a href="${link}">Organizer</a>.`
  const description = currDesc
    ? `${currDesc} <html-blob><br/><br/>${linkWithText}</html-blob>`
    : linkWithText

  updateEvent(item, { description });
}

/**
 * Update event properties, but only if the event wasn't changed upstream.
 */
export function updateEvent(item, update) {
  const eventId = item._id.replace('gcal:', '');
  const path = { calendarId: item.calendarId, eventId };
  const headers = { 'If-Match': item.event.etag };
  var request = gapi.client.calendar.events.patch(path, update, headers);

  request.execute(function (response) {
    if (response.status && !response.error) {
      console.log(`Event ${item._id} updated.`);
      // Update the item's event data, including new etag for next api calls.
      Object.assign(item.event, response);
      item.save();
    } else {
      console.error(`Failed to update event ${eventId}`, response)
      let msg = `Error: couldn't update event "${item.name}" in Google Calendar.`;
      if (response.code === 412) {
        msg += " Event has changed in the meantime.";
      }
      alert(msg);
    }
  });
}

export function exportToCalendar(item) {
  if (!item.plannedFor) {
    alert("Cannot export to calendar something that has no date.");
    return;
  }
  if (item.plannedFor.parentClass !== 'day') {
    alert("Exporting items not planned for a specific day is not yet supported.");
    return;
  }

  const link = `${window.location.origin}/app/task/${item._id}`;
  const descr = `Event exported from <a href="${link}">Organizer</a>.`;
  const data = {
    'summary': item.name,
    'description': descr,
    'start': {},
    'end': {},
    'extendedProperties': {
      'shared': {
        'organizerId': item._id,
      }
    },
  };

  // full-day vs timed event
  const pf = item.plannedFor.parentKey;
  if (item.dur) {
    const start = moment(pf).add(6, "hours");
    const end = start.clone().add(15 * item.dur, "minutes");
    data.start.dateTime = start.utc();
    data.end.dateTime = end.utc();
  } else {
    data.start.date = pf;
    data.end.date = moment(pf).add(1, "days").format(isoDateFormat);
  }

  var request = gapi.client.calendar.events.insert({
    'calendarId': 'primary',
    'resource': data,
  });
  request.execute(function (event) {
    console.log(event)
    if (event?.status === "confirmed") {
      console.log('Event created!');
      item.gcal_id = event.id
      item.gcal_link = event.htmlLink
      item.save()
    } else {
      alert('Something went wrong :(');
      console.log(event)
    }
  });
}
